/** @jsx jsx */
import React, { useMemo } from 'react';
import { jsx } from '@emotion/core';
import { splitEvery, defaultTo } from 'ramda';

import { FormValues } from './CardChoice';

import { Form, Field, FormikProps, FieldProps } from 'formik';
import Grid, { GridSize } from '@material-ui/core/Grid';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from 'components/Button';
import PageContainer from 'components/PageContainer';

import { CardChoice, Tenant } from 'state/tenant';
import useTranslate from 'hooks/useTranslate';
import {
  useMarkdownMultilineTranslate,
  useMarkdownTranslate,
} from 'hooks/useTranslate/useTranslate';

import styles from './styles';
import {
  CardContent,
  Container,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import CancelButton from 'features/CancelButton';
import ActionButtonContainer from 'components/ActionButtonsContainer';
import Radio from 'components/Radio';
import Title from 'components/Title';
import ExpandableLink from 'components/ExpandableLink';
import StickyFooter from 'components/StickyFooter/StickyFooter';
import { composeStyles, Theme } from 'features/Theme';
import { useTheme } from 'emotion-theming';

interface CardOptionProps {
  cardChoice: CardChoice;
  radioChecked: boolean;
}

const CardOption: React.FC<CardOptionProps> = props => {
  const { cardChoice, radioChecked } = props;

  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();

  const theme = useTheme<Theme>();

  // There seems to be a very strange issue with Emotion css props.
  // If we pass to css a function that accept a theme, the component will
  // completely unmount its children every time we toggle that class.
  //
  // However, if we pass it a style that is already computed, on the the class
  // name will change, and the children do not re-render unncessarily.
  const selectedStyle = useMemo(() => styles.cardOptionCardSelected(theme), [
    theme,
  ]);

  return (
    <div css={radioChecked ? selectedStyle : null}>
      <CardContent>
        <FormControlLabel
          value={cardChoice.code.toString()}
          css={styles.radioInput}
          control={
            <Radio
              css={styles.radioInput}
              color="primary"
              id={`cardChoice-${cardChoice.code}`}
            />
          }
          label={
            <div css={styles.cardLabelColumn}>
              {/* This extra div is necessary for IE 11 */}
              <div css={styles.cardImageContainer}>
                <img
                  alt="Card Choice Option"
                  css={styles.cardOptionImage}
                  src={cardChoice.imageUrl}
                />
              </div>
              <p css={styles.cardOptionLabel}>
                {tMd(`cardChoice.choices.${cardChoice.code}.label`)}
              </p>
            </div>
          }
        />
        {!t(`cardChoice.choices.${cardChoice.code}.sublabel`) ? null : (
          <div
            css={composeStyles([
              styles.cardOptionSubLabel,
              styles.benefitsList,
            ])}
          >
            {tMdMultiline(`cardChoice.choices.${cardChoice.code}.sublabel`)}
          </div>
        )}
        {!t(`cardChoice.choices.${cardChoice.code}.benefits`) ? null : (
          <div css={styles.expandableBenefitsLabel}>
            <ExpandableLink
              expandAbove={true}
              expandedTitle={t(`cardChoice.benefitsLabelExpanded`)}
              collapsedTitle={t(`cardChoice.benefitsLabelCollapsed`)}
              id={`offerDetails-${cardChoice.name}`}
              addIcon={true}
            >
              <div
                css={composeStyles([styles.benefitBody, styles.benefitsList])}
              >
                {tMdMultiline(`cardChoice.choices.${cardChoice.code}.benefits`)}
              </div>
            </ExpandableLink>
          </div>
        )}
      </CardContent>
    </div>
  );
};

const CardRow: React.FC = ({ children }) => (
  <Grid
    container
    direction="row"
    justify="center"
    alignItems="flex-start"
    spacing={3}
    css={styles.cardPickerRow}
  >
    {children}
  </Grid>
);

const CardGridItem: React.FC<
  Partial<Record<Breakpoint, boolean | GridSize>>
> = ({ children, ...sizingProps }) => (
  <Grid
    container
    item
    direction="column"
    justify="center"
    alignItems="center"
    {...sizingProps}
  >
    {children}
  </Grid>
);

type PropTypes = FormikProps<FormValues> & {
  cardChoices: Tenant['enabledFeatures']['cardChoices'];
};

const CardChoiceView: React.FC<PropTypes> = props => {
  const { isSubmitting, isValid, cardChoices } = props;
  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const perRow = 2;
  const columnWidth = Math.floor(12 / perRow) as GridSize;
  const rows = splitEvery(perRow, cardChoices);
  const tMdMultiline = useMarkdownMultilineTranslate();

  return (
    <Form>
      <PageContainer maxWidth="sm">
        <div css={styles.headingContainer}>
          <Title css={styles.titleText}> {tMd('cardChoice.title')} </Title>

          {!t('cardChoice.subtitle') ? null : (
            <div css={styles.normalText}>{tMd('cardChoice.subtitle')}</div>
          )}
          {!t('cardChoice.offerDetails') ? null : (
            <div css={styles.expandableDetailLabel}>
              <ExpandableLink
                expandedTitle={t('cardChoice.offerDetailsLabelExpanded')}
                collapsedTitle={t('cardChoice.offerDetailsLabelCollapsed')}
                id="offerDetails"
              >
                <Container maxWidth="sm">
                  <div css={styles.offerDetailText}>
                    {tMdMultiline('cardChoice.offerDetails')}
                  </div>
                </Container>
              </ExpandableLink>
            </div>
          )}
        </div>
        <Typography>{t('cardChoice.cardSelectionSubtitle')}</Typography>
        <Grid
          container
          direction="row"
          justify="center"
          alignItems="center"
          css={styles.cardPickerContainer}
        >
          <Field name="cardChoice">
            {(fieldProps: FieldProps) => {
              return (
                <RadioGroup
                  {...fieldProps.field}
                  css={styles.radioGroup}
                  value={defaultTo('', fieldProps.field.value).toString()}
                >
                  {rows.map((choices, index) => (
                    <CardRow key={index}>
                      {choices.map(cardChoice => (
                        <CardGridItem
                          xs={12}
                          sm={columnWidth}
                          key={cardChoice.code}
                        >
                          <CardOption
                            radioChecked={
                              cardChoice.code.toString() ===
                              fieldProps.field.value
                            }
                            cardChoice={cardChoice}
                          />
                        </CardGridItem>
                      ))}
                    </CardRow>
                  ))}
                </RadioGroup>
              );
            }}
          </Field>
        </Grid>

        <Grid container direction="row" justify="center" alignItems="center">
          <Grid xs={12} item={true}>
            <div css={styles.bottomDisclosure}>
              {!t('cardChoice.bottomDisclosure')
                ? null
                : tMdMultiline('cardChoice.bottomDisclosure')}
              {!t('cardChoice.bottomDisclosureFinePrint') ? null : (
                <ExpandableLink
                  expandedTitle={t(`cardChoice.bottomDisclosureLabelExpanded`)}
                  collapsedTitle={t(
                    `cardChoice.bottomDisclosureLabelCollapsed`,
                  )}
                  id="bottomDisclosure-fineprint"
                >
                  <div css={styles.bottomDisclosureFinePrint}>
                    {tMdMultiline('cardChoice.bottomDisclosureFinePrint')}
                  </div>
                </ExpandableLink>
              )}
            </div>
          </Grid>
        </Grid>

        <StickyFooter>
          <div css={styles.stickyFooter}>
            <ActionButtonContainer>
              <Button
                mobileFullWidth
                type="submit"
                id="cardChoiceSubmit"
                disabled={!isValid || isSubmitting}
              >
                {t('cardChoice.nextButton')}
              </Button>

              <CancelButton />
            </ActionButtonContainer>
          </div>
        </StickyFooter>
      </PageContainer>
    </Form>
  );
};

export default CardChoiceView;
